import { useState, useCallback, useEffect } from "react";

const STORAGE_KEY = "corebase_onboarding";

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveState(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {}
}

/**
 * Hook para manejar el estado del onboarding tour.
 * @param {Array} steps - Array de pasos: { id, title, description, targetId, action?, tab? }
 */
export default function useOnboarding(steps) {
  const [state, setState] = useState(() => {
    const saved = loadState();
    if (saved && saved.completed) return { active: false, step: 0, completed: true, dismissed: false };
    if (saved && saved.dismissed) return { active: false, step: 0, completed: false, dismissed: true };
    return { active: true, step: 0, completed: false, dismissed: false };
  });

  useEffect(() => {
    saveState(state);
  }, [state]);

  const currentStep = state.active ? steps[state.step] || null : null;
  const totalSteps = steps.length;
  const stepNumber = state.step + 1;

  const next = useCallback(() => {
    setState((prev) => {
      if (prev.step + 1 >= steps.length) {
        return { active: false, step: 0, completed: true, dismissed: false };
      }
      return { ...prev, step: prev.step + 1 };
    });
  }, [steps.length]);

  const prev = useCallback(() => {
    setState((prev) => {
      if (prev.step <= 0) return prev;
      return { ...prev, step: prev.step - 1 };
    });
  }, []);

  const dismiss = useCallback(() => {
    setState({ active: false, step: 0, completed: false, dismissed: true });
  }, []);

  const restart = useCallback(() => {
    setState({ active: true, step: 0, completed: false, dismissed: false });
  }, []);

  return {
    active: state.active,
    currentStep,
    stepNumber,
    totalSteps,
    completed: state.completed,
    dismissed: state.dismissed,
    next,
    prev,
    dismiss,
    restart,
  };
}
